import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useHandleRequest } from "../../../../hooks/useHandleRequest";

/**
 * Staged deny flow for the request approval modal.
 *
 * `queueDeny` marks the nurse locally — no API call. `queueRestore` removes the nurse from the
 * queue before Submit. `flushDenies()` (called on Submit) commits the queue in a single request;
 * `clearQueue()` (called on close) discards it.
 *
 * Server-side already-denied nurses come back on `NurseData.isDenied` and are rendered read-only;
 * they don't flow through this hook.
 */
export const useDenyNurseFromRequest = (requestId: string) => {
	const { t } = useTranslation();
	const { decline } = useHandleRequest();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	const [queuedNurseIds, setQueuedNurseIds] = useState<number[]>([]);

	const queueDeny = useCallback((nurseId: number) => {
		setQueuedNurseIds((prev) => (prev.includes(nurseId) ? prev : [...prev, nurseId]));
	}, []);

	const queueRestore = useCallback((nurseId: number) => {
		setQueuedNurseIds((prev) => prev.filter((id) => id !== nurseId));
	}, []);

	const clearQueue = useCallback(() => setQueuedNurseIds([]), []);

	const flushDenies = useCallback(async () => {
		if (queuedNurseIds.length === 0) return;
		const ids = queuedNurseIds;
		setQueuedNurseIds([]);
		track("Staffing - request rejected", { source: "request_approval", request_id: requestId, count: ids.length, nurse_ids: ids.join(",") });
		try {
			await decline({ requestId, nurseIds: ids });
		} catch {
			trackError("Staffing - request reject failed", { request_id: requestId, count: ids.length, nurse_ids: ids.join(",") });
			showSnackbar(t("deny.staff.error"), "error");
			return;
		}
		showSnackbar(ids.length === 1 ? t("deny.staff.success") : t("deny.staff.success.plural", { count: ids.length }), "success");
	}, [queuedNurseIds, requestId, decline, showSnackbar, track, trackError, t]);

	return { queueDeny, queueRestore, queuedNurseIds, flushDenies, clearQueue };
};
